import React, { useState } from 'react';
import {
  Alert, Box, Button, Chip, Divider, Grid, IconButton,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography
} from '@mui/material';
import {
  Add as AddIcon, Build as PartIcon, CloudUpload as UploadIcon, ZoomIn as ZoomIcon,
  Sms as SmsIcon, Visibility as ViewIcon, PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import WorkshopWorkflowPanels from './WorkshopWorkflowPanels';
import PartPhotoImage from '../../components/PartPhotoImage';
import { submitQaCheck, markRepairCollected } from '../../services/api';
import { getErrorMessage } from '../../utils/errorMessage';
import {
  canManageRepairs, isTechnician, isTechnicalCoordinator, isAdminOfficer
} from '../../utils/auth';

const canQa = () =>
  isTechnician() || canManageRepairs() || isTechnicalCoordinator() || isAdminOfficer();

const fmtWhen = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const Fact = ({ label, value }) => (
  <Grid item xs={12} sm={6}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
      {value || '—'}
    </Typography>
  </Grid>
);

const RepairStepDetails = ({
  data,
  expandedStep,
  setExpandedStep,
  images = [],
  partRequests = [],
  outsourceTechList = [],
  onReload,
  setError,
  setSuccess,
  showQuoteInvoice,
  onUploadImages,
  onImageClick,
  onRequestPart,
  onApprovePart,
  onRejectPart,
  canApproveParts,
  fmt,
  onSms,
  smsSending,
  hasQuotation,
  hasInvoice,
  isWarranty,
  warrantyExtrasBillable,
  onViewInvoice,
  onGenerateInvoice,
  invoiceGenerating,
  onCreateQuotation,
  onEditQuotation,
  onViewQuotation
}) => {
  const navigate = useNavigate();
  const [qaRemark, setQaRemark] = useState('');
  const [qaBusy, setQaBusy] = useState(false);
  const [collectBusy, setCollectBusy] = useState(false);

  const qaStatus = (data.qa_status || '').toLowerCase();
  const qaDone = ['passed', 'pass', 'failed', 'fail'].includes(qaStatus);
  const qaPassed = qaStatus === 'passed' || qaStatus === 'pass';
  const qaFailed = qaStatus === 'failed' || qaStatus === 'fail';

  const handleQa = async (result) => {
    if (result === 'fail' && !qaRemark.trim()) {
      setError('Enter a remark when QA fails');
      return;
    }
    try {
      setQaBusy(true);
      await submitQaCheck(data.id, { result, remark: qaRemark.trim() });
      setSuccess(result === 'pass' ? 'QA passed — ready to collect' : 'QA failed — stays on the QA queue');
      setQaRemark('');
      onReload();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to submit QA'));
    } finally {
      setQaBusy(false);
    }
  };

  const handleCollect = async () => {
    if (!window.confirm(`Mark JOB ${data.grn_number || data.service_number} as collected / closed?`)) return;
    try {
      setCollectBusy(true);
      await markRepairCollected(data.id);
      setSuccess('Job marked as collected');
      onReload();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to mark collected'));
    } finally {
      setCollectBusy(false);
    }
  };

  const coverage =
    data.service_coverage === 'warranty' || data.is_warranty
      ? `Warranty${data.warranty_action ? ` · ${data.warranty_action}` : ''}`
      : 'Billable';

  if (expandedStep === 1) {
    return (
      <Box>
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Fact label="Received" value={data.received_date ? new Date(data.received_date).toLocaleDateString('en-GB') : fmtWhen(data.created_at)} />
          <Fact label="JOB number" value={data.grn_number || data.service_number} />
          <Fact label="Customer" value={[data.customer_name, data.customer_phone].filter(Boolean).join(' · ')} />
          <Fact label="Coverage" value={coverage} />
          <Fact label="Device" value={[data.device_type, data.device_brand, data.device_model].filter(Boolean).join(' · ')} />
          <Fact label="Serial / IMEI" value={data.device_serial || data.device_imei || data.incoming_serial_number} />
          <Fact label="Physical condition" value={data.physical_condition} />
          <Fact label="Accessories received" value={data.accessories_received} />
          <Fact label="Reported issue" value={data.device_issue} />
          <Fact label="Check-in notes" value={data.notes} />
        </Grid>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="subtitle2" fontWeight={700}>Job photos</Typography>
          <Button component="label" variant="outlined" startIcon={<UploadIcon />} size="small">
            Upload
            <input type="file" hidden multiple accept="image/*" onChange={onUploadImages} />
          </Button>
        </Box>
        {images.length > 0 ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' },
              gap: 1
            }}
          >
            {images.map((img) => (
              <Box key={img.id} sx={{ cursor: 'pointer', position: 'relative', borderRadius: 1, overflow: 'hidden', height: 120 }}>
                <img
                  src={img.file_url}
                  alt={img.file_name}
                  loading="lazy"
                  style={{ objectFit: 'cover', height: '100%', width: '100%' }}
                  onClick={() => onImageClick(img)}
                />
                <IconButton
                  size="small"
                  sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'rgba(0,0,0,0.5)', color: '#fff' }}
                  onClick={() => onImageClick(img)}
                >
                  <ZoomIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        ) : (
          <Typography color="text.secondary" variant="body2">No photos yet — upload the item as received.</Typography>
        )}
      </Box>
    );
  }

  if ([2, 3, 4].includes(expandedStep)) {
    return (
      <Box>
        <WorkshopWorkflowPanels
          data={data}
          expandedStep={expandedStep}
          setExpandedStep={setExpandedStep}
          outsourceTechList={outsourceTechList}
          onReload={onReload}
          setError={setError}
          setSuccess={setSuccess}
          showQuoteInvoice={showQuoteInvoice}
          embedded
          onCreateQuotation={onCreateQuotation}
          onEditQuotation={onEditQuotation}
          onViewQuotation={onViewQuotation}
          hasQuotation={hasQuotation}
        />
        {expandedStep === 4 && (
          <Box sx={{ mt: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2">
                <PartIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                Part requests
              </Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={onRequestPart}>Request part</Button>
            </Box>
            {partRequests.length === 0 ? (
              <Typography variant="caption" color="text.secondary">No part requests for this job.</Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Part</TableCell>
                    <TableCell>Photo</TableCell>
                    <TableCell>Qty</TableCell>
                    <TableCell>Price</TableCell>
                    <TableCell>Status</TableCell>
                    {canApproveParts && <TableCell>Action</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {partRequests.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell>{p.product_name}</TableCell>
                      <TableCell>
                        {p.part_photo_url ? (
                          <PartPhotoImage
                            photoUrl={p.part_photo_url}
                            onClick={() => onImageClick({ photoUrl: p.part_photo_url, label: p.product_name })}
                          />
                        ) : '—'}
                      </TableCell>
                      <TableCell>{p.quantity}</TableCell>
                      <TableCell>{fmt(p.unit_price)}</TableCell>
                      <TableCell>
                        <Chip
                          label={p.status}
                          size="small"
                          color={p.status === 'approved' ? 'success' : p.status === 'pending' ? 'warning' : 'default'}
                        />
                      </TableCell>
                      {canApproveParts && p.status === 'pending' && (
                        <TableCell>
                          <Button size="small" color="success" onClick={() => onApprovePart(p)}>Approve</Button>
                          <Button size="small" color="error" onClick={() => onRejectPart(p.id)}>Reject</Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        )}
      </Box>
    );
  }

  if (expandedStep === 5) {
    return (
      <Box>
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Fact
            label="QA result"
            value={
              qaPassed ? 'Passed' : qaFailed ? 'Failed' : data.ready_for_collection ? 'Marked complete with repair' : 'Not tested yet'
            }
          />
          <Fact label="Checked by" value={data.qa_checked_by} />
          <Fact label="Checked at" value={data.qa_checked_at ? fmtWhen(data.qa_checked_at) : ''} />
          <Fact label="QA remark" value={data.qa_remark} />
          <Fact label="Repair summary" value={data.repair_completion_summary} />
          <Fact
            label="Ready for collection"
            value={data.ready_for_collection ? fmtWhen(data.ready_for_collection_at) : 'Not yet'}
          />
        </Grid>
        {qaPassed && <Alert severity="success" sx={{ mb: 2 }}>Final testing passed.</Alert>}
        {qaFailed && <Alert severity="error" sx={{ mb: 2 }}>QA failed — fix the issue and re-test.</Alert>}
        {!data.ready_for_collection && !qaDone && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Finish the repair in Step 4 first. Testing is marked when the job is sent for QA or ready to collect.
          </Alert>
        )}
        {canQa() && data.ready_for_collection && !qaPassed && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="subtitle2" gutterBottom>Record QA on this job</Typography>
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              label="QA remark"
              value={qaRemark}
              onChange={(e) => setQaRemark(e.target.value)}
              sx={{ mb: 1.5 }}
            />
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button variant="contained" color="success" disabled={qaBusy} onClick={() => handleQa('pass')}>
                Pass QA
              </Button>
              <Button variant="outlined" color="error" disabled={qaBusy} onClick={() => handleQa('fail')}>
                Fail QA
              </Button>
              <Button variant="text" onClick={() => navigate('/repairs/qa')}>Open QA queue</Button>
            </Box>
          </Box>
        )}
      </Box>
    );
  }

  if (expandedStep === 6) {
    const logs = data.smsLogs || [];
    return (
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Tell the customer the job status. Each send is logged here so you can see what was already done.
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Button variant="contained" startIcon={<SmsIcon />} disabled={smsSending} onClick={() => onSms('progress')}>
            SMS progress
          </Button>
          {showQuoteInvoice && (
            <>
              <Button variant="outlined" startIcon={<SmsIcon />} disabled={smsSending} onClick={() => onSms('quotation')}>
                SMS quotation
              </Button>
              <Button variant="outlined" startIcon={<SmsIcon />} disabled={smsSending} onClick={() => onSms('invoice')}>
                SMS invoice
              </Button>
            </>
          )}
        </Box>
        {data.customer_approval_channel && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Customer approved via {data.customer_approval_channel}
            {data.customer_approval_remark ? ` — ${data.customer_approval_remark}` : ''}
          </Alert>
        )}
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>Notification log</Typography>
        {logs.length === 0 ? (
          <Typography color="text.secondary" variant="body2">No SMS sent yet.</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Phone</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.map((s) => (
                <TableRow key={s.id}>
                  <TableCell>{fmtWhen(s.created_at)}</TableCell>
                  <TableCell>{(s.message_type || 'general').replace(/_/g, ' ')}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={s.status}
                      color={s.status === 'sent' ? 'success' : s.status === 'failed' ? 'error' : 'default'}
                    />
                  </TableCell>
                  <TableCell>{s.phone || data.customer_phone || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>
    );
  }

  return (
    <Box>
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Fact label="Invoice" value={data.invoice_number || (hasInvoice ? 'Generated' : 'Not generated')} />
        <Fact label="Payment" value={(data.payment_status || 'pending').replace(/_/g, ' ')} />
        <Fact
          label="Amount"
          value={isWarranty && !warrantyExtrasBillable ? 'FREE (Warranty)' : fmt(data.total_amount)}
        />
        <Fact label="Collected at" value={data.collected_at ? fmtWhen(data.collected_at) : 'Not collected'} />
        <Fact label="Collected by" value={data.collected_by} />
        <Fact
          label="Ready since"
          value={data.ready_for_collection_at ? fmtWhen(data.ready_for_collection_at) : data.ready_for_collection ? 'Yes' : 'Not ready'}
        />
      </Grid>
      {data.customer_rejected_quotation && (
        <Alert severity="warning" sx={{ mb: 2 }}>Customer rejected the quotation — item not repaired.</Alert>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        {showQuoteInvoice && (
          hasInvoice ? (
            <Button variant="contained" color="success" startIcon={<ViewIcon />} onClick={onViewInvoice}>
              View invoice{data.invoice_number ? ` (${data.invoice_number})` : ''}
            </Button>
          ) : (
            (!isWarranty || warrantyExtrasBillable) && (
              <Button
                variant="contained"
                color="success"
                startIcon={<PdfIcon />}
                disabled={invoiceGenerating}
                onClick={onGenerateInvoice}
              >
                {invoiceGenerating ? 'Generating...' : 'Generate invoice'}
              </Button>
            )
          )
        )}
        {data.ready_for_collection && !data.collected_at && (
          <Button variant="contained" color="primary" disabled={collectBusy} onClick={handleCollect}>
            {collectBusy ? 'Saving...' : 'Mark collected'}
          </Button>
        )}
        <Button variant="outlined" onClick={() => navigate('/repairs/ready-to-collect')}>
          Ready to collect list
        </Button>
      </Box>
      {data.collected_at && (
        <Alert severity="success">Handed over to the customer.</Alert>
      )}
      {!data.ready_for_collection && !data.collected_at && (
        <Alert severity="info">This job is not ready for handover yet. Complete repair (Step 4) first.</Alert>
      )}
    </Box>
  );
};

export default RepairStepDetails;
